/**
 * Client entry point
 *
 * @fileoverview Mounts the React application with React Query and React Router providers
 *
 * @changeHistory
 * - 2025-11-20: Lint fixes - Added root element guard to avoid non-null assertion (GitHub Copilot)
 * - 2026-09-11: Added PostHog tracing headers for AgentPortal API requests (Codex)
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { PostHogProvider } from 'posthog-js/react';
import posthog, { CapturedNetworkRequest } from 'posthog-js';
import { ThemeProvider } from '@shared/components/ThemeProvider';
import App from './App';
import './styles/index.css';

// Initialize PostHog
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
const apiHostname = new URL(
  (import.meta.env.VITE_API_URL as string | undefined) || '/api/v1',
  window.location.origin
).hostname;

/* v8 ignore start */
if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: posthogHost, // || 'https://us.i.posthog.com',
    autocapture: true,
    capture_pageview: true,
    tracing_headers: [apiHostname],
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
      maskCapturedNetworkRequestFn: (request: CapturedNetworkRequest) => {
        // For example: ignoring a request entirely
        if (request.name.includes('cloudteamrealty.com')) {
          return null;
        }

        // ... or remove the query string from the URL
        const [nameWithoutQuery] = request.name.split('?');
        request.name = nameWithoutQuery;

        // ...redact the request or response body **however makes sense for your payloads**
        request.requestBody = request.requestBody?.replace(
          /"password":\s*"[^"]*"/g,
          '"password": "redacted-password"'
        );
        request.responseBody = request.responseBody?.replace(
          /"password":\s*"[^"]*"/g,
          '"password": "redacted-password"'
        );

        // ... or remove the request body
        request.requestBody = undefined;

        // ... and more

        // finally return the request
        return request;
      },
    },
  });

  posthog.register({
    app_name: 'agent-portal-client',
    environment: import.meta.env.VITE_NODE_ENV || 'development',
    version: import.meta.env.PACKAGE_VERSION || 'unknown',
  });
}
/* v8 ignore stop */

// Configure React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root DOM element with id "root" was not found.');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
      <QueryClientProvider client={queryClient}>
        <PostHogProvider client={posthog}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </PostHogProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>
);
